import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import FaceIdentityBrandLogo from '../components/FaceIdentityBrandLogo';
import FaceIdentityFlowNav from '../components/FaceIdentityFlowNav';
import { useFaceIdentityOrgLogo } from '../hooks/useFaceIdentityOrgLogo';
import { useFaceIdentityScrollShell } from '../hooks/useFaceIdentityScrollShell';
import { useKioskLandingConfig } from '../hooks/useKioskLandingConfig';
import {
  confirmSalonFaceMatch,
  lookupFaceUserByMobile,
  persistFacePending,
  persistFaceProfileSnapshot,
  readFaceCaptureBlob,
  readFacePending,
  resolveFaceOrganizationId,
  submitPassportReturningCheckin,
  submitPassportPendingRegistration,
} from '../utils/faceIdentityApi';
import { disablePassportKioskScanAfterCapture } from '../utils/passportKioskApi';
import CountryWhatsAppInput from '../../../shared/components/CountryWhatsAppInput';
import { DEFAULT_WHATSAPP_COUNTRY } from '../../../shared/utils/whatsappPhone';
import { linkPendingCheckinIfNeeded } from '../../salon/utils/visitHandoff';
import { linkFaceToCheckin } from '../../partner/utils/checkinQueueApi';
import { buildKioskNavState } from '../utils/kioskNavState';
import { faceIdentityRoute, isPassportCheckinBoardFlow, isPassportFlow, useFaceIdentityBase } from '../utils/faceIdentityRoutes';
import { isFranchisePassportCheckinEnabled } from '../../shared/franchiseFlowAuth';
import { resolveFacePortraitUrl } from '../utils/facePortrait';
import {
  KIOSK_PAGE_KEYS,
  showMobileLookupOption,
} from '../utils/kioskLandingDefaults';
import { setSalonGuestPii, purgeLegacySalonPiiSessionKeys } from '../../salon/utils/salonGuestPiiStore';
import {
  setSalonFaceUserId,
  setSalonComparePrevious,
  setSalonFaceOrg,
  setSalonEntrySource,
} from '../../salon/utils/salonFaceSession';
import { resolveSalonHomePath } from '../../salon/utils/salonHomeNav';
import { resolvePresentationAwareHomePath } from '../../presentation/presentationSession';
import {
  isRegisterMobileComplete,
  mobileValidationError,
  normalizeRegisterMobileValue,
} from '../../shared/mobileDigits';
import { SALON_HUB_PATH } from '../../salon/config/salonApp';
import '../styles/FaceIdentityLuxTheme.css';
import '../styles/FaceIdentityScrollShell.css';
import '../pages/FaceIdentityRegisterPage.css';
import '../../salon/pages/SalonCustomerCodePage.css';

/**
 * Mobile lookup check-in — shared by Face Identity and Salon kiosk flows.
 * @param {{ variant?: 'face-identity' | 'salon' }} props
 */
export default function FaceIdentityMobileLookupPage({ variant = 'face-identity' }) {
  useFaceIdentityScrollShell();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgLogoUrl, showLogo, onLogoError } = useFaceIdentityOrgLogo();
  const [mobile, setMobile] = useState('');
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [guestSummary, setGuestSummary] = useState(null);
  const pendingNav = readFacePending();
  const fromFaceUnknown = Boolean(
    location.state?.fromFaceUnknown || pendingNav?.fromFaceUnknown,
  );
  const fromFaceFamiliar = Boolean(
    location.state?.fromFaceFamiliar || pendingNav?.fromFaceFamiliar,
  );
  const isSalon = variant === 'salon' || location.state?.mode === 'salon';
  const isPassport = isPassportCheckinBoardFlow(location.pathname);
  const isPassportKiosk = isPassportFlow(location.pathname);
  const fiBase = useFaceIdentityBase();
  const pageKey = isSalon ? KIOSK_PAGE_KEYS.SALON_ENTRY : KIOSK_PAGE_KEYS.FACE_IDENTITY;
  const { config, loading: configLoading } = useKioskLandingConfig(pageKey);
  const directPassportCheckin = isPassportKiosk && !isFranchisePassportCheckinEnabled();
  const showMobile =
    directPassportCheckin ||
    showMobileLookupOption(config) ||
    fromFaceUnknown ||
    fromFaceFamiliar;
  const backPath = fromFaceUnknown || fromFaceFamiliar
    ? (isSalon ? '/nyafaz-ep/salon/scan' : faceIdentityRoute('scan', location.pathname))
    : isSalon
      ? '/nyafaz-ep/salon/start'
      : faceIdentityRoute('start', location.pathname);
  const scanPath = isSalon ? '/nyafaz-ep/salon/scan' : faceIdentityRoute('scan', location.pathname);
  const registerPath = isSalon
    ? '/nyafaz-ep/salon/register'
    : faceIdentityRoute('register', location.pathname);
  const welcomePath = isSalon ? '/nyafaz-ep/salon/welcome' : faceIdentityRoute('welcome', location.pathname);
  const homePath = isSalon
    ? resolveSalonHomePath({ preferStartWhenIdle: true })
    : resolvePresentationAwareHomePath(location.pathname, location.state);

  const navState = useMemo(
    () =>
      buildKioskNavState(location.state, {
        mode: isSalon ? 'salon' : location.state?.mode || 'frontdesk',
        next: isSalon ? location.state?.next || SALON_HUB_PATH : location.state?.next || null,
      }),
    [isSalon, location.state],
  );

  useEffect(() => {
    if (configLoading) return;
    if (showMobile) return;
    navigate(isSalon ? backPath : scanPath, { replace: true, state: navState });
  }, [configLoading, showMobile, isSalon, backPath, scanPath, navigate, navState]);

  const onLookup = async (event) => {
    event.preventDefault();
    const e164 = normalizeRegisterMobileValue(mobile);
    if (!isRegisterMobileComplete(e164)) {
      setError(mobileValidationError());
      setNotFound(false);
      return;
    }
    setError('');
    setNotFound(false);
    setGuestSummary(null);
    setSubmitting(true);
    try {
      const data = await lookupFaceUserByMobile(e164);
      const profile = data.profile || data.user?.profile || null;
      const lastRef =
        data.user?.last_salon_reference ||
        data.user?.lastSalonReference ||
        profile?.last_salon_reference ||
        '';
      const lastServices =
        data.user?.last_selected_services ||
        profile?.last_selected_services ||
        data.last_selected_services ||
        [];
      const serviceNames = Array.isArray(lastServices)
        ? lastServices
            .map((s) => (typeof s === 'string' ? s : s?.name || s?.label || ''))
            .filter(Boolean)
        : [];
      setGuestSummary({
        name: data.user?.name || data.user?.full_name || 'Guest',
        lastRef,
        serviceNames,
      });

      const faceBlob = readFaceCaptureBlob();
      if (faceBlob && (fromFaceUnknown || fromFaceFamiliar)) {
        try {
          await confirmSalonFaceMatch(data.user.id, faceBlob);
        } catch (_) {
          /* non-blocking — profile still loads */
        }
      }

      const pendingCheckinId =
        location.state?.pendingCheckinId || location.state?.checkinId || null;
      if (isSalon && pendingCheckinId) {
        try {
          await linkPendingCheckinIfNeeded(data.user.id, {
            pendingCheckinId,
            name: data.user.name || data.user.full_name,
            mobile: data.user.mobile,
          });
        } catch (_) {
          /* non-blocking */
        }
      }

      const portraitUrl = resolveFacePortraitUrl(data.user, data.profile, data);
      const captureFromFace =
        location.state?.captureUrl || pendingNav?.captureUrl || portraitUrl || undefined;
      const snapshot = {
        faceUserId: data.user.id,
        user: data.user,
        profile,
        confidence: data.confidence ?? 1,
        mode: 'returning',
        fromMobileLookup: true,
        preselectedServices: lastServices,
        captureUrl: captureFromFace,
      };
      persistFaceProfileSnapshot(snapshot);
      persistFacePending(snapshot);

      setSalonFaceUserId(data.user.id);
      setSalonComparePrevious(true);
      setSalonGuestPii({
        mobile: data.user.mobile,
        name: data.user.name || data.user.full_name,
      });
      purgeLegacySalonPiiSessionKeys();
      const hqOrg =
        resolveFaceOrganizationId() || data.organization_id || data.user.organization_id;
      if (hqOrg) setSalonFaceOrg(hqOrg);
      setSalonEntrySource(isSalon ? 'salon_mobile' : 'face_identity');

      let passportCheckinId = pendingCheckinId || null;
      if (isPassport) {
        try {
          if (isPassportKiosk) await disablePassportKioskScanAfterCapture();
          if (pendingCheckinId) {
            await linkFaceToCheckin(pendingCheckinId, {
              faceUserId: data.user.id,
              customerSnapshot: {
                registration_status: 'complete',
                face_user_id: data.user.id,
                name: data.user.name || data.user.full_name,
                full_name: data.user.name || data.user.full_name,
                mobile: data.user.mobile,
                registered_by: 'passport_mobile_lookup',
                registered_at: new Date().toISOString(),
              },
            });
            passportCheckinId = pendingCheckinId;
          } else {
            const queued = await submitPassportReturningCheckin({
              user: data.user,
              profile,
              captureUrl: captureFromFace,
              isNewMember: false,
            });
            passportCheckinId = queued?.checkin?.id ?? passportCheckinId;
            if (queued?.reused) {
              navigate(welcomePath, {
                replace: true,
                state: {
                  ...snapshot,
                  ...navState,
                  isNewMember: false,
                  preselectedServices: lastServices,
                  lastVisitReference: lastRef || undefined,
                  captureUrl: captureFromFace,
                  checkinId: passportCheckinId || undefined,
                  passportGuestShown: true,
                  passportDuplicateVisit: true,
                  checkin: queued?.checkin || undefined,
                },
              });
              setSubmitting(false);
              return;
            }
          }
        } catch (queueErr) {
          setError(queueErr?.message || 'Could not notify front desk — try again');
          setSubmitting(false);
          return;
        }
      }

      navigate(welcomePath, {
        replace: true,
        state: {
          ...snapshot,
          ...navState,
          isNewMember: false,
          preselectedServices: lastServices,
          lastVisitReference: lastRef || undefined,
          captureUrl: captureFromFace,
          pendingCheckinId: pendingCheckinId || undefined,
          checkinId: isPassport ? passportCheckinId || undefined : pendingCheckinId || undefined,
          checkinCode: location.state?.checkinCode || location.state?.pendingCheckinCode,
          fromCustomerCode: Boolean(pendingCheckinId),
          fromFaceUnknown: undefined,
          passportGuestShown: isPassport ? true : undefined,
        },
      });
    } catch (err) {
      if (err?.code === 'NOT_FOUND') {
        setNotFound(true);
        setError(err.message || 'No profile found for this mobile number');
      } else {
        setNotFound(false);
        setError(err?.message || 'Lookup failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="salon-code-page page-bg fi-lux-theme fi-scroll-page">
      <div className="salon-code-page__stage">
        <header className="salon-code-page__brand">
          <FaceIdentityBrandLogo
            showLogo={showLogo}
            orgLogoUrl={orgLogoUrl}
            onLogoError={onLogoError}
            imgClassName="salon-code-page__logo"
            fallbackClassName="salon-code-page__logo-text"
          />
          <FaceIdentityFlowNav
            onBack={() => navigate(backPath, { replace: true, state: navState })}
            homePath={homePath}
          />
        </header>

        {configLoading || !showMobile ? (
          <p className="salon-code-page__lede">Redirecting…</p>
        ) : (
        <form className="salon-code-page__form" onSubmit={onLookup}>
          <p className="salon-code-page__eyebrow">
            {fromFaceFamiliar
              ? 'Face looks familiar'
              : fromFaceUnknown
                ? 'Face not recognized'
                : 'Mobile check-in'}
          </p>
          <h1 className="salon-code-page__title">Enter your mobile number</h1>
          <p className="salon-code-page__lede">
            {fromFaceFamiliar
              ? 'We think we recognize you — enter the mobile number on your profile to continue.'
              : fromFaceUnknown
                ? "We couldn't match your face — enter the mobile number on your profile to continue."
                : "We'll find your saved profile and continue check-in — no face capture needed."}
          </p>

          <label className="salon-code-page__label" htmlFor="fi-mobile-lookup">
            Mobile number
          </label>
          <div className="salon-code-page__phone fi-register__phone">
            <CountryWhatsAppInput
              id="fi-mobile-lookup"
              value={normalizeRegisterMobileValue(mobile)}
              defaultCountry={DEFAULT_WHATSAPP_COUNTRY}
              placeholder="Mobile number with country code"
              containerClassName="fi-register__phone-input salon-code-page__phone-input"
              disabled={submitting}
              onChange={(phoneE164) => setMobile(phoneE164)}
            />
          </div>

          {error ? <p className="salon-code-page__error">{error}</p> : null}

          {guestSummary ? (
            <p className="salon-code-page__lede">
              Welcome back, {guestSummary.name}
              {!isPassport && guestSummary.lastRef ? ` · last visit ${guestSummary.lastRef}` : ''}
              {guestSummary.serviceNames?.length
                ? ` · previously: ${guestSummary.serviceNames.slice(0, 3).join(', ')}`
                : ''}
            </p>
          ) : null}

          {notFound ? (
            <button
              type="button"
              className="salon-code-page__submit salon-code-page__submit--ghost"
              disabled={submitting}
              onClick={async () => {
                if (isPassport && (fromFaceFamiliar || fromFaceUnknown)) {
                  setSubmitting(true);
                  setError('');
                  try {
                    const captureUrl =
                      location.state?.captureUrl || pendingNav?.captureUrl || undefined;
                    await (isPassportKiosk ? disablePassportKioskScanAfterCapture() : Promise.resolve());
                    const queued = await submitPassportPendingRegistration({ captureUrl });
                    navigate(faceIdentityRoute('pending-registration', location.pathname), {
                      replace: true,
                      state: {
                        ...navState,
                        captureUrl,
                        referenceToken: queued.referenceToken,
                        checkinId: queued.checkin?.id || null,
                        passportGuestShown: true,
                        fromFaceFamiliar: undefined,
                        fromFaceUnknown: undefined,
                      },
                    });
                  } catch (err) {
                    setError(err?.message || 'Could not start new-guest registration');
                  } finally {
                    setSubmitting(false);
                  }
                  return;
                }
                if (isPassport) {
                  navigate(scanPath, { replace: false, state: navState });
                  return;
                }
                const checkedMobile = normalizeRegisterMobileValue(mobile);
                const regState = {
                  ...navState,
                  ...location.state,
                  mode: 'register',
                  registerReason: fromFaceUnknown || fromFaceFamiliar ? 'unknown' : 'enroll',
                  registerMessage:
                    fromFaceUnknown || fromFaceFamiliar
                      ? 'No profile for this number — create one to continue.'
                      : 'Create your profile to continue.',
                  captureUrl: location.state?.captureUrl || pendingNav?.captureUrl,
                  prefillMobile: checkedMobile,
                  guestMobile: checkedMobile,
                  lookupMobile: checkedMobile,
                  fromFaceUnknown: undefined,
                  fromFaceFamiliar: undefined,
                };
                persistFacePending(regState);
                navigate(registerPath, { replace: false, state: regState });
              }}
            >
              {isPassport && (fromFaceFamiliar || fromFaceUnknown)
                ? 'Continue as new guest at front desk'
                : isPassport
                  ? 'Scan again'
                  : fromFaceUnknown || fromFaceFamiliar
                    ? 'Register as new guest'
                    : 'Capture face to register'}
            </button>
          ) : null}

          <button type="submit" className="salon-code-page__submit" disabled={submitting}>
            {submitting ? 'Looking up…' : 'Continue'}
          </button>

          <button
            type="button"
            className="salon-code-page__back"
            onClick={() => navigate(backPath, { replace: true, state: navState })}
          >
            Back
          </button>
        </form>
        )}
      </div>
    </div>
  );
}
